import {
	brandPrimary,
	captchaDisplayBackground,
	captchaDisplayBorder,
	captchaDisplayBorderWidth,
	captchaDisplayRadius,
	textColor,
} from "./tokens";

/** CSS variable names — set on `:root` via `bootstrapShellTheme`. */
export const CSS_VAR_AUTH = {
	textColor: "--psda-text-color",
	brandPrimary: "--psda-brand-primary",
} as const;

/** Main heading on Login, Signup, Forgot password, OTP, Reset password cards. */
export const authPageHeadingClassName =
	"auth-page-heading font-['Poppins'] text-4xl font-semibold tracking-tight text-ink short:text-[1.625rem] short:leading-tight";

/** Heading band inside auth card. */
export const authCardHeadingBandClassName =
	"auth-card-heading-band shrink-0 px-6 pb-3 pt-5 text-center sm:px-10 sm:pb-4 sm:pt-6 short:px-5 short:pb-2 short:pt-4";

export const authCardHeadingSubtextClassName =
	"auth-card-subtext mt-[8px] text-[14px] font-normal leading-[1.5] text-[#494949] short:mt-[6px] short:text-[13px]";

/** Right-hand panel — no page scroll; grey background stays fixed. */
export const authFormPanelClassName =
	"auth-form-panel relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden lg:w-1/2";

/** Centers the form column — 80% width with responsive horizontal padding. */
export const authFormCenterWrapClassName =
	"auth-form-center flex min-h-0 w-[80%] max-w-full flex-1 flex-col items-center justify-center self-center overflow-hidden px-4 py-8 sm:px-6 md:px-8 short:py-4";

/** Shared max width for login, signup, forgot password, OTP, and reset password cards. */
export const authCardMaxWidthClassName = "w-full max-w-[46rem] sm:max-w-[52rem]";

/** Max-height wrapper so the card can scroll internally when needed. */
export const authCardOuterClassName = "auth-card-outer mx-auto flex min-h-0 max-h-full w-full flex-col";

/** Card shell — transparent, no border/shadow; scroll lives in `.auth-form-body`. */
export const authCardClassName =
	"auth-card relative flex w-full min-h-0 max-h-full flex-col overflow-hidden rounded-2xl border-0 bg-transparent shadow-none";

/** Form body padding below the heading band. */
export const authFormBodyClassName =
	"auth-form-body flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-5 pb-5 pt-4 sm:px-6 sm:pt-5 short:px-4 short:pb-4 short:pt-3";

export const authFooterClassName =
	"shrink-0 px-5 pb-5 pt-2 text-center text-base text-[#8a8a8a] sm:px-6 short:px-4 short:pb-4 short:text-sm";

/** Tighter vertical rhythm on short viewports. */
export const authFormStackClassName = "space-y-4 short:space-y-2";

export const authFormStackLooseClassName = "space-y-4 short:space-y-2";

export const authFormGridGapClassName = "gap-3 short:gap-3";

/** Shared label sizing — matches employer portal (text-sm, sm:text-base, semibold). */
export const authFieldLabelClassName = "auth-field-label text-sm font-semibold text-gray-900 sm:text-base";

/** Full label element — must match InputField label markup exactly. */
export const authFormLabelClassName = `mb-2 block font-semibold ${authFieldLabelClassName}`;

/** Auth pages only — login, signup, forgot password, OTP, reset password (10px corners). */
export const authButtonBorderRadius = 10;
export const authButtonRadiusClassName = "rounded-auth-button";
export const authSubmitButtonStyle = { borderRadius: authButtonBorderRadius } as const;

export const authSubmitButtonClassName =
	"auth-submit-button h-[44px] min-h-[44px] w-full rounded-auth-button bg-primary py-0 font-['Poppins'] text-base font-semibold text-white shadow-sm hover:bg-[#025c9a] short:h-10 short:min-h-10";

/** Signup / forgot-password link row — margins enforced in index.css. */
export const authLinkRowClassName = "auth-link-row flex flex-wrap items-center justify-between gap-3";

export const authSignupLinkClassName =
	"font-bold text-gray-800 transition-colors hover:opacity-90";

export const authForgotPasswordLinkClassName =
	"text-sm font-semibold text-primary transition-colors hover:text-primary-hover";

/** Shared width for verify-OTP code inputs, submit button, and resend row. */
export const authOtpBlockClassName =
	"auth-otp-block mx-auto w-full max-w-[min(100%,22rem)] sm:max-w-[24rem]";

export const authRootErrorClassName =
	"mb-6 rounded-lg border border-red-200 bg-red-50 p-4 short:mb-2 short:p-2.5";

/** Wrap forms to shrink inputs/helper text on short screens (see index.css). */
export const authFormCompactClassName = "auth-form-compact";

/** Captcha code display container (shared Login + Signup). */
export const captchaDisplayClassName =
	"flex items-center justify-center px-3 sm:min-h-[3.25rem] short:min-h-[2.5rem] short:px-2 [&_canvas]:block [&_canvas]:max-h-[2.75rem] [&_canvas]:w-auto short:[&_canvas]:max-h-[2.25rem]";

export const captchaDisplayStyle = {
	borderRadius: captchaDisplayRadius,
	border: `${captchaDisplayBorderWidth}px solid ${captchaDisplayBorder}`,
	background: captchaDisplayBackground,
} as const;

/** Refresh button beside captcha — matches captcha display + field border radius. */
export const captchaRefreshButtonClassName =
	"inline-flex w-auto max-w-full shrink-0 items-center justify-center gap-1 rounded-lg border-[1.2px] border-[#D9D9D9] bg-white px-3 text-base font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-[3.25rem] short:min-h-[2.5rem] short:px-2 short:text-sm";

export const authCaptchaRefreshButtonStyle = { borderRadius: captchaDisplayRadius } as const;

/** @deprecated Use authFormLabelClassName — kept for captcha imports. */
export const authCaptchaLabelClassName = authFormLabelClassName;

/** Space between captcha image row and captcha text input (not above the label). */
export const authCaptchaInputWrapClassName = "mt-2";

export { textColor, brandPrimary };
